import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { AdTopBanner, AdInContent, AdBottomBanner } from '../components/AdSense';

const RetirementCalculatorPage = () => {
  const [currentAge, setCurrentAge] = useState('30');
  const [retirementAge, setRetirementAge] = useState('65');
  const [currentSavings, setCurrentSavings] = useState('50000');
  const [monthlyContribution, setMonthlyContribution] = useState('500');
  const [annualReturn, setAnnualReturn] = useState('7');
  const [inflationRate, setInflationRate] = useState('2.5');
  const [desiredIncome, setDesiredIncome] = useState('5000');
  const [result, setResult] = useState(null);

  const calculate = () => {
    const years = parseFloat(retirementAge) - parseFloat(currentAge);
    const savings = parseFloat(currentSavings) || 0;
    const monthly = parseFloat(monthlyContribution) || 0;
    const rate = (parseFloat(annualReturn) || 0) / 100;
    const inflation = (parseFloat(inflationRate) || 0) / 100;
    const income = parseFloat(desiredIncome) || 0;
    const monthlyRate = rate / 12;
    const months = years * 12;

    // Future value of current savings
    const fvSavings = savings * Math.pow(1 + rate, years);
    
    // Future value of monthly contributions
    const fvContributions = monthly * ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate);
    
    const totalAtRetirement = fvSavings + fvContributions;
    const totalContributions = savings + (monthly * months);
    const totalInterest = totalAtRetirement - totalContributions;
    
    // Inflation adjusted value
    const inflationAdjusted = totalAtRetirement / Math.pow(1 + inflation, years);
    
    // How many years will savings last (4% withdrawal rule)
    const annualWithdrawal = income * 12;
    const yearsWillLast = Math.log((totalAtRetirement * 0.04) / annualWithdrawal * rate + 1) / Math.log(1 + rate);
    
    // Required savings for desired income (25x rule)
    const requiredForIncome = annualWithdrawal * 25;
    const shortfall = requiredForIncome - totalAtRetirement;

    setResult({
      totalAtRetirement,
      totalContributions,
      totalInterest,
      inflationAdjusted,
      yearsToRetirement: years,
      requiredForIncome,
      shortfall: shortfall > 0 ? shortfall : 0,
      onTrack: shortfall <= 0,
      monthlyIncomeFrom4Percent: (totalAtRetirement * 0.04) / 12
    });
  };

  const formatCurrency = (num) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(num);

  return (
    <div className="min-h-screen bg-[#0d0d0d] flex flex-col">
      <Header />
      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <AdTopBanner />
          
          <nav className="text-sm text-gray-500 mb-4">
            <Link to="/home" className="text-cyan-400 hover:text-cyan-300">Home</Link>
            <span className="mx-2">›</span>
            <span className="text-gray-400">Retirement Calculator</span>
          </nav>

          <h1 className="text-3xl font-bold text-white mb-4">Retirement Calculator</h1>
          <p className="text-gray-400 mb-6">Plan your retirement by calculating how much you'll have saved by retirement age.</p>

          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <label className="text-gray-300 w-36">Current Age</label>
                  <Input type="number" value={currentAge} onChange={(e) => setCurrentAge(e.target.value)}
                    className="w-24 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                </div>
                <div className="flex items-center gap-4">
                  <label className="text-gray-300 w-36">Retirement Age</label>
                  <Input type="number" value={retirementAge} onChange={(e) => setRetirementAge(e.target.value)}
                    className="w-24 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                </div>
                <div className="flex items-center gap-4">
                  <label className="text-gray-300 w-36">Current Savings</label>
                  <div className="flex items-center gap-1">
                    <span className="text-gray-400">$</span>
                    <Input type="number" value={currentSavings} onChange={(e) => setCurrentSavings(e.target.value)}
                      className="w-32 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <label className="text-gray-300 w-36">Monthly Contribution</label>
                  <div className="flex items-center gap-1">
                    <span className="text-gray-400">$</span>
                    <Input type="number" value={monthlyContribution} onChange={(e) => setMonthlyContribution(e.target.value)}
                      className="w-32 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <label className="text-gray-300 w-36">Annual Return</label>
                  <div className="flex items-center gap-1">
                    <Input type="number" step="0.1" value={annualReturn} onChange={(e) => setAnnualReturn(e.target.value)}
                      className="w-20 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                    <span className="text-gray-400">%</span>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <label className="text-gray-300 w-36">Inflation Rate</label>
                  <div className="flex items-center gap-1">
                    <Input type="number" step="0.1" value={inflationRate} onChange={(e) => setInflationRate(e.target.value)}
                      className="w-20 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                    <span className="text-gray-400">%</span>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <label className="text-gray-300 w-36">Desired Monthly Income</label>
                  <div className="flex items-center gap-1">
                    <span className="text-gray-400">$</span>
                    <Input type="number" value={desiredIncome} onChange={(e) => setDesiredIncome(e.target.value)}
                      className="w-32 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                  </div>
                </div>
              </div>
            </div>

            <Button onClick={calculate} className="mt-6 bg-cyan-600 hover:bg-cyan-500 text-white px-8">Calculate</Button>

            {result && (
              <div className="mt-6 p-5 bg-[#0d0d0d] border border-[#2a2a2a] rounded-xl">
                <div className="text-center mb-6">
                  <p className="text-gray-400 mb-2">Projected Retirement Savings</p>
                  <p className="text-4xl font-bold text-green-400">{formatCurrency(result.totalAtRetirement)}</p>
                  <p className="text-gray-500 text-sm">After {result.yearsToRetirement} years</p>
                </div>

                <div className="grid md:grid-cols-2 gap-4 mb-6">
                  <div className="p-4 bg-[#1a1a1a] rounded-lg">
                    <p className="text-gray-400 text-sm">Total Contributions</p>
                    <p className="text-white font-semibold">{formatCurrency(result.totalContributions)}</p>
                  </div>
                  <div className="p-4 bg-[#1a1a1a] rounded-lg">
                    <p className="text-gray-400 text-sm">Total Interest Earned</p>
                    <p className="text-cyan-400 font-semibold">{formatCurrency(result.totalInterest)}</p>
                  </div>
                  <div className="p-4 bg-[#1a1a1a] rounded-lg">
                    <p className="text-gray-400 text-sm">Inflation-Adjusted Value</p>
                    <p className="text-white font-semibold">{formatCurrency(result.inflationAdjusted)}</p>
                  </div>
                  <div className="p-4 bg-[#1a1a1a] rounded-lg">
                    <p className="text-gray-400 text-sm">Monthly Income (4% Rule)</p>
                    <p className="text-white font-semibold">{formatCurrency(result.monthlyIncomeFrom4Percent)}</p>
                  </div>
                </div>

                <div className={`p-4 rounded-lg ${result.onTrack ? 'bg-green-500/10 border border-green-500/30' : 'bg-amber-500/10 border border-amber-500/30'}`}>
                  {result.onTrack ? (
                    <p className="text-green-400">✓ You're on track to meet your retirement income goal!</p>
                  ) : (
                    <p className="text-amber-400">⚠ You may be {formatCurrency(result.shortfall)} short of your goal. Consider increasing contributions.</p>
                  )}
                </div>
              </div>
            )}
          </div>

          <AdInContent />

          <div className="mt-10 p-6 bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl">
            <h3 className="text-lg font-semibold text-white mb-4">Financial Calculators</h3>
            <div className="flex flex-wrap gap-2">
              {[{ name: 'Investment', path: '/investment-calculator' }, { name: 'Compound Interest', path: '/compound-interest-calculator' }, { name: 'Salary', path: '/salary-calculator' }].map((calc) => (
                <Link key={calc.path} to={calc.path} className="px-3 py-1.5 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-cyan-400 hover:text-cyan-300 text-sm">{calc.name}</Link>
              ))}
            </div>
          </div>
          <AdBottomBanner />
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default RetirementCalculatorPage;
